import { FormEvent, useEffect, useState } from 'react';
import { Markdown } from '../../components/Markdown';
import { getStatus, sendChat, type ChatMessage } from '../../services/api/chat';
import type { Card, DeckId } from '../../types';

interface Props {
  active: { deckId: DeckId; card: Card } | null;
}

const STORAGE_KEY = 'klausurphase.chat.v2';

export function ChatPanel({ active }: Props) {
  const [open, setOpen] = useState(false);
  const [messagesByCard, setMessagesByCard] = useState<Record<string, ChatMessage[]>>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [hasKey, setHasKey] = useState(true);
  const [model, setModel] = useState('gpt-4o-mini');
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeImage, setIncludeImage] = useState(true);
  const key = active ? `${active.deckId}#${active.card.id}` : null;
  const messages = key ? messagesByCard[key] ?? [] : [];

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(messagesByCard)); }, [messagesByCard]);
  useEffect(() => {
    document.body.classList.toggle('chat-open', open);
    return () => document.body.classList.remove('chat-open');
  }, [open]);
  useEffect(() => {
    if (!open) return;
    getStatus().then((status) => { setHasKey(!!status.hasKey); if (status.model) setModel(status.model); }).catch(() => setHasKey(false));
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!active || !key || !input.trim() || busy) return;
    const nextMessages = [...messages, { role: 'user', content: input.trim() } satisfies ChatMessage].slice(-20);
    setMessagesByCard((prev) => ({ ...prev, [key]: nextMessages }));
    setInput('');
    setBusy(true);
    try {
      const response = await sendChat({ deckId: active.deckId, card: active.card, includeSummary, includeImage, model, messages: nextMessages });
      const assistantMessage: ChatMessage = { role: 'assistant', content: response.reply };
      setMessagesByCard((prev) => ({ ...prev, [key]: [...(prev[key] ?? nextMessages), assistantMessage].slice(-20) }));
    } catch (err) {
      const content = `❌ Fehler: ${err instanceof Error ? err.message : 'Chat fehlgeschlagen.'}`;
      const assistantMessage: ChatMessage = { role: 'assistant', content };
      setMessagesByCard((prev) => ({ ...prev, [key]: [...(prev[key] ?? nextMessages), assistantMessage].slice(-20) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {open && <button className="chat-backdrop" aria-label="Chat schließen" onClick={() => setOpen(false)} />}
      <button className={`chat-toggle chat-fab ${open ? 'active' : ''}`} onClick={() => setOpen((value) => !value)} title="KI-Tutor"><span>🤖</span><span className="chat-fab-label">KI-Tutor</span></button>
      <aside className={`chat-panel ${open ? 'open' : ''}`}>
        <div className="chat-head">
          <div className="chat-head-title"><span className="chat-ico">🤖</span><div><strong>{active ? `Karte ${active.card.number}` : 'KI-Tutor'}</strong><p className="muted">{active?.card.chapter ?? 'Keine Karte aktiv'}</p></div></div>
          <button className="btn-ghost chat-close" onClick={() => setOpen(false)}>×</button>
        </div>
        <div className="chat-controls chat-options">
          <label className="chat-check"><input type="checkbox" checked={includeSummary} onChange={(event) => setIncludeSummary(event.target.checked)} /> Summary</label>
          <label className="chat-check"><input type="checkbox" checked={includeImage} onChange={(event) => setIncludeImage(event.target.checked)} /> Bild</label>
          <select className="select small chat-model-select" value={model} onChange={(event) => setModel(event.target.value)} aria-label="KI-Modell wählen">
            <optgroup label="OpenAI · schnell & günstig">
              <option value="gpt-4o-mini">GPT-4o mini</option>
              <option value="gpt-4.1-mini">GPT-4.1 mini</option>
              <option value="gpt-4.1-nano">GPT-4.1 nano</option>
            </optgroup>
            <optgroup label="OpenAI · stark">
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4.1">GPT-4.1</option>
            </optgroup>
            <optgroup label="OpenAI · Reasoning">
              <option value="o4-mini">o4-mini</option>
              <option value="o3-mini">o3-mini</option>
            </optgroup>
          </select>
          <button type="button" className="btn-ghost small chat-clear" onClick={() => key && setMessagesByCard((prev) => ({ ...prev, [key]: [] }))} title="Verlauf leeren">Leeren</button>
        </div>
        {!hasKey && <div className="chat-keyhint chat-key-hint">Kein OpenAI-Key konfiguriert.</div>}
        <div className="chat-body">
          {!messages.length && <div className="chat-empty"><h3>Frag den KI-Tutor</h3><p>{active ? 'Nutze eine der Schnellfragen oder stelle eine eigene Frage.' : 'Wähle eine Karte im Lernen oder in der Kartenansicht.'}</p><div className="chat-suggests">{['Erkläre mir die Karte einfach.', 'Gib mir ein Beispiel.', 'Welche Eselsbrücke passt dazu?'].map((suggestion) => <button key={suggestion} className="chat-suggest" disabled={!active} onClick={() => setInput(suggestion)}>{suggestion}</button>)}</div></div>}
          {messages.map((message, index) => <div key={index} className={`chat-msg ${message.role === 'user' ? 'chat-user' : 'chat-bot'}`}><div className="chat-bubble">{message.role === 'assistant' ? <Markdown text={message.content} /> : message.content}</div></div>)}
          {busy && <div className="chat-msg chat-bot"><div className="chat-bubble chat-typing"><span /> <span /> <span /> Antwort wird generiert…</div></div>}
        </div>
        <form className="chat-input chat-input-row" onSubmit={submit}>
          <textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder="Frage zur aktuellen Karte…" />
          <button className="chat-send" disabled={!active || busy} title="Senden" aria-label="Senden">
            {busy ? <span className="chat-send-spinner" /> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11.5L21 3l-8.5 18-2.2-7.3z" /><path d="M12.5 11.5L21 3" /></svg>}
          </button>
        </form>
      </aside>
    </>
  );
}
